/**
 * uploadService — thin wrapper around POST /attachments/upload.
 *
 * Sends a file as multipart/form-data to the GuestGuru backend,
 * returns the stored attachment (with a proper /uploads/<uuid>.ext URL).
 *
 * Use this wherever FileReader.readAsDataURL() was previously used —
 * it eliminates base64 blobs from the database.
 */

import { STORAGE_KEYS } from '../constants/storageKeys';
import { planningPath } from './PlanningApiClient';

// Mirrors PlanningApiClient.ts's getApiUrl() exactly — this used to read
// process.env.REACT_APP_GG_API_URL (a CRA-style var only usable here
// via vite.config.ts's define shim) while every other service reads
// import.meta.env.VITE_GG_API_URL directly. Same env var Vite
// natively supports, no shim indirection to silently drift out of sync.
function getApiUrl(): string {
  return (
    (import.meta.env.VITE_GG_API_URL as string | undefined) ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:8083'
      : 'https://gg.ainaeco.uk')
  );
}

/**
 * Resolve a media path from GuestGuru storage to a full URL.
 * Paths returned by the backend are relative (/uploads/...) — this
 * converts them using the same base URL logic as the API client.
 */
export function resolveMediaUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('blob:')) {
    return path;
  }
  return `${getApiUrl()}${path}`;
}

function getApiKey(): string {
  const key = import.meta.env.VITE_ANS_WEB_API_KEY as string | undefined;
  if (!key) console.error('VITE_ANS_WEB_API_KEY is not configured — GuestGuru requests will be rejected.');
  return key ?? '';
}

// /attachments routes require planner-level role on write — x-user-email
// alone is a viewer-only fallback, so writes need the real GuestGuru JWT too.
// Only set when a real profile owner is actually stored — defaulting to a
// real person's email let any unauthenticated session silently identify
// as them and get viewer access with zero login.
/**
 * fetch() that reports a network failure in words a user can act on.
 * A dropped connection rejects with a bare TypeError whose message is the
 * browser's own ("Failed to fetch" / "Load failed"), which reached users
 * verbatim wherever a handler surfaces err.message.
 */
async function netFetch(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new Error("Couldn't reach the server. Check your connection and try again.");
  }
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'x-api-key': getApiKey() };
  const profileOwner = localStorage.getItem(STORAGE_KEYS.PROFILE_OWNER_EMAIL);
  if (profileOwner) headers['x-user-email'] = profileOwner;
  const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Attachment {
  id: string;
  project_id?: string | null;
  /** Older servers only send this spelling. */
  wedding_id?: string | null;
  entity_type: string;
  entity_id: string;
  url: string;
  storage_key: string | null;
  original_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  label: string | null;
  sort_order: number;
  uploaded_at: string;
  uploaded_by: string | null;
  is_rate_card: boolean;
}

export interface UploadOptions {
  entityType: string;
  /** Entity ID. For new records not yet saved, pass a temp UUID (e.g. crypto.randomUUID()). */
  entityId: string;
  label?: string;
  sortOrder?: number;
  isRateCard?: boolean;
}

// ── API calls ─────────────────────────────────────────────────────────────────

/**
 * Upload a single image file.
 * Returns the full attachment record including the served `url`.
 */
export async function uploadImage(file: File, options: UploadOptions): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entity_type', options.entityType);
  formData.append('entity_id', options.entityId);
  // No project_id field: the server resolves the project from the Bearer
  // token. It cannot read one from a multipart body anyway — RBAC runs
  // before multer parses it — so sending it here only looked like it worked.
  if (options.label)                   formData.append('label',       options.label);
  if (options.sortOrder !== undefined) formData.append('sort_order',  String(options.sortOrder));
  if (options.isRateCard)              formData.append('is_rate_card', 'true');

  const response = await netFetch(`${getApiUrl()}${planningPath('/attachments')}/upload`, {
    method: 'POST',
    // No Content-Type header — let the browser set the multipart boundary
    headers: authHeaders(),
    body: formData,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Upload failed' })) as { error?: string };
    throw new Error(body.error ?? 'Upload failed');
  }

  return response.json() as Promise<Attachment>;
}

/**
 * Fetch all attachments for a given entity.
 */
export async function getAttachments(
  entityType: string,
  entityId: string,
  /** Return archived (soft-deleted) attachments instead of live ones. */
  archived = false,
): Promise<Attachment[]> {
  const url = `${getApiUrl()}${planningPath('/attachments')}?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}${archived ? '&archived=true' : ''}`;
  const response = await netFetch(url, {
    headers: authHeaders(),
  });

  if (!response.ok) throw new Error('Failed to fetch attachments');
  return response.json() as Promise<Attachment[]>;
}

/**
 * Update an attachment's label and/or is_rate_card flag — the file itself
 * is immutable once uploaded.
 */
export async function updateAttachment(id: string, updates: { label?: string; isRateCard?: boolean }): Promise<Attachment> {
  const response = await netFetch(`${getApiUrl()}${planningPath('/attachments')}/${id}`, {
    method: 'PATCH',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ label: updates.label, is_rate_card: updates.isRateCard }),
  });

  if (!response.ok) throw new Error('Failed to update attachment');
  return response.json() as Promise<Attachment>;
}

/**
 * Delete an attachment by ID (also removes the file from disk on the server).
 */
/** Soft-delete: the row and the file both survive and can be restored. */
export async function deleteAttachment(id: string): Promise<void> {
  const response = await netFetch(`${getApiUrl()}${planningPath('/attachments')}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) throw new Error('Failed to archive attachment');
}

/** Bring an archived attachment back into the live list. */
export async function restoreAttachment(id: string): Promise<void> {
  const response = await netFetch(`${getApiUrl()}${planningPath('/attachments')}/${id}?restore=true`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error('Failed to restore attachment');
}

/**
 * Unlinks the file from disk and drops the row — genuinely irreversible.
 * The server refuses unless the attachment is already archived, so this can
 * never be reached in a single step.
 */
export async function deleteAttachmentPermanently(id: string): Promise<void> {
  const response = await netFetch(`${getApiUrl()}${planningPath('/attachments')}/${id}?permanent=true`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to delete attachment');
  }
}
